import {colors, fonts, textVariants, tokens} from '@commercial-helios/ui/theme'
import React from 'react'
import styled, {css} from 'styled-components'

import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {focusStyles} from '@helios/shared/styles/utils'
import {fluidType} from '@helios/shared/utils/fluid'

export interface TextProps {
    color?: keyof typeof colors.text
    center?: boolean
    upperCase?: boolean
    variant?: keyof typeof textVariants
    'data-testid'?: string
}

const upperCaseStyles = css`
    text-transform: uppercase;
`

const centerStyles = css`
    text-align: center;
`

export const getTextVariantStyles = (variant: NonNullable<TextProps['variant']>) => css`
    font-family: ${textVariants[variant].fontFamily};
    font-weight: ${textVariants[variant].fontWeight};
    line-height: ${textVariants[variant].lineHeight};

    ${fluidType('font-size', textVariants[variant].fontSize)}
`

type FormatContent = <T>(
    raw: T,
    replaceContent: (arg: string) => string,
) => string | T | React.ReactElement | React.ReactChild[]

const formatContent: FormatContent = (raw, replaceContent) => {
    if (typeof raw === 'string') {
        return replaceContent(raw)
    }

    if (React.isValidElement<any>(raw)) {
        return React.cloneElement(
            raw,
            [],
            raw.props.children && Array.isArray(raw.props.children)
                ? raw.props.children.map((child: React.ReactChild) =>
                      formatContent(child, replaceContent),
                  )
                : formatContent(raw.props.children, replaceContent),
        )
    }

    if (Array.isArray(raw)) {
        return raw.map((el) => formatContent(el, replaceContent))
    }

    return raw
}

export const text = ({
    color,
    center,
    upperCase,
    variant = 'paragraph-large',
}: TextProps = {}) => css`
    margin: 0;
    font-family: inherit;
    font-weight: normal;
    line-height: 1.6;
    color: ${color ? colors.text[color] : 'inherit'};
    word-break: break-word;

    ${center && centerStyles}
    ${upperCase && upperCaseStyles}
    ${variant && getTextVariantStyles(variant)}
`

export const StyledText = styled.p.withConfig({
    shouldForwardProp: (prop) => {
        return !['size', 'weight', 'font', 'color', 'center', 'upperCase', 'variant'].includes(prop)
    },
})<TextProps>(text)

export const Text = React.forwardRef<HTMLElement, React.ComponentProps<typeof StyledText>>(
    ({id, children, ...props}, ref) => {
        const globalStrings = useGlobalStrings()
        const primaryFontVariants = Object.keys(textVariants).filter(
            (variant) =>
                textVariants[variant as NonNullable<TextProps['variant']>].fontFamily ===
                fonts.families.primary,
        )

        const primaryFont = 'primary'

        const isPrimaryVariant =
            (!props.variant && !props.font) ||
            primaryFontVariants.includes(props.variant) ||
            props.font === primaryFont
        const replaceContent = (text: string) =>
            text.replace('{{numberOfCustomers}}', globalStrings.num_customers)

        return (
            <StyledText
                {...props}
                id={id}
                ref={ref}
                {...(isPrimaryVariant && {'data-font': primaryFont})}
            >
                {formatContent(children, replaceContent)}
            </StyledText>
        )
    },
) as typeof StyledText // have to typecast to make TS happy, but it should be safe

export interface TextLinkProps extends TextProps {
    bgColor?: keyof typeof colors.link
}

export const TextLink = styled.a.withConfig({
    shouldForwardProp: (prop) => {
        return !['color', 'center', 'upperCase', 'variant', 'bgColor', 'id'].includes(prop)
    },
})<TextLinkProps>(
    ({bgColor = 'pink', ...props}) => css`
        ${text(props)}

        padding: 0;
        color: ${colors.link[bgColor].foreground};
        text-align: inherit;
        text-decoration: underline;
        cursor: pointer;
        background: ${colors.link[bgColor].background};
        border: none;
        transition: background ${tokens.fastIn};

        :hover,
        :active {
            color: ${colors.link[bgColor].hover.foreground};
            background: ${colors.link[bgColor].hover.background};
        }

        :focus {
            ${focusStyles()}
            border-radius: ${tokens.arc4};
        }
    `,
)

type NavTextLinkProps = {color?: keyof typeof colors.text}

export const NavTextLink = styled(TextLink)<NavTextLinkProps>`
    color: ${(props) => (props.color ? colors.text[props.color] : colors.link.pink.foreground)};
    text-decoration: none;
    background: ${colors.link.pink.background};

    :hover,
    :active {
        color: ${colors.link.pink.foreground};
        text-decoration: underline;
        background: ${colors.link.pink.background};
    }

    :focus {
        ${focusStyles()}
        border-radius: ${tokens.arc4};
    }
`
